import { API_BASE_URL } from './api';

export const checkEmailAvailability = async (email) => {
    const response = await fetch(`${API_BASE_URL}/auth/check-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
    });
    if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Email check failed');
    }
    return await response.json();
};

export const signup = async (signupData) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/signup`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(signupData),
        });

        if (!response.ok) {
            const errorData = await response.json();
            
            // Handle Pydantic validation errors
            if (errorData.detail && Array.isArray(errorData.detail)) {
                const validationErrors = errorData.detail.map(error => {
                    const field = error.loc ? error.loc[error.loc.length - 1] : 'field';
                    const message = error.msg || 'Invalid value';
                    
                    // Extract clean message from Pydantic's verbose format
                    if (message.includes('value is not a valid email address:')) {
                        // Extract just the part after the colon
                        const cleanMessage = message.split(': ').slice(1).join(': ');
                        return cleanMessage;
                    }
                    
                    // For other messages, keep as-is or add field context if needed
                    if (message.includes('String should have') || 
                        message.includes('field required')) {
                        return message;
                    }
                    
                    return `${field}: ${message}`;
                });
                throw new Error(validationErrors.join(', '));
            }
            
            // Handle single error message
            if (typeof errorData.detail === 'string') {
                throw new Error(errorData.detail);
            }
            
            throw new Error('Signup failed');
        }

        return await response.json();
    } catch (error) {
        console.error('Signup API error:', error);
        throw error;
    }
};


export const signin = async (email, password) => {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/signin`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email,
                password,
            }),
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Signin failed');
        }

        return await response.json();
    } catch (error) {
        console.error('Signin API error:', error);
        throw error;
    }
};

export const requestPasswordReset = async (email) => {
  const response = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({ email }),
  });

  if (!response.ok) throw new Error('Password reset request failed');
  return await response.json();
};

export const resetPassword = async (token, new_password, confirm_password) => {
  const response = await fetch(`${API_BASE_URL}/auth/reset-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: JSON.stringify({
      token,
      new_password,
      confirm_password,
    }),
  });

  if (!response.ok) {
    throw new Error('Password reset failed');
  }

  return await response.json();
};

export const logout = async (token) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Logout failed');
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('isAdmin');

    return await response.json();
  } catch (err) {
    console.error('Logout error:', err);
    throw err;
  }
};

export const logoutAll = async (token) => {
  const response = await fetch(`${API_BASE_URL}/auth/logout-all`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    credentials: 'include',
  });

  if (!response.ok) throw new Error('Logout from all sessions failed');
  return await response.json();
};

export const validateSession = async (token) => {
  const response = await fetch(`${API_BASE_URL}/auth/validate-session`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) throw new Error('Session validation failed');
  return await response.json();
};

export const getSessionInfo = async (token) => {
  const response = await fetch(`${API_BASE_URL}/auth/session-info`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) throw new Error('Failed to fetch session info');
  return await response.json();
};

export const refreshToken = async (token) => {
  const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  });

  if (!response.ok) throw new Error('Token refresh failed');
  return await response.json();
};